import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { 
  Activity, Shield, FileText, Clock, AlertTriangle,
  TrendingUp, TrendingDown, CheckCircle2, XCircle, Users
} from 'lucide-react';
import HealthScoreExplainer from '@/components/dashboard/HealthScoreExplainer';

const STATUS_CONFIG = {
  excellent: { label: 'Excellent', color: 'text-green-700', bg: 'bg-green-100', ring: 'ring-green-400' },
  good: { label: 'Good', color: 'text-amber-700', bg: 'bg-amber-100', ring: 'ring-amber-400' },
  attention_needed: { label: 'Needs Attention', color: 'text-orange-700', bg: 'bg-orange-100', ring: 'ring-orange-400' },
  critical: { label: 'Critical', color: 'text-red-700', bg: 'bg-red-100', ring: 'ring-red-400' }
};

function ScoreRing({ score, size = 48, strokeWidth = 4, color }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E6E0D9"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

function BreakdownBar({ label, value, icon: Icon }) {
  const getBarColor = (val) => {
    if (val >= 80) return 'bg-green-500';
    if (val >= 60) return 'bg-amber-500';
    if (val >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-[#8C8078] shrink-0" />
      <span className="text-xs text-[#5D524D] w-28 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-[#E6E0D9] rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all duration-500", getBarColor(value))}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="text-xs font-medium text-[#2C2420] w-8 text-right">{value}</span>
    </div>
  );
}

function ClientHealthCard({ health, onClick }) {
  const cfg = STATUS_CONFIG[health.status] || STATUS_CONFIG.critical;

  return (
    <div
      className="bg-white rounded-lg border border-[#E6E0D9] p-4 hover:shadow-md transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`health-card-${health.clientId}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <ScoreRing score={health.score} color={health.color} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#2C2420] truncate group-hover:text-[#4A3B32]">
            {health.clientName}
          </p>
          <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", cfg.bg, cfg.color)}>
            {cfg.label}
          </span>
        </div>
      </div>

      <div className="space-y-1.5">
        <BreakdownBar label="Data" value={health.breakdown.dataCompleteness} icon={FileText} />
        <BreakdownBar label="Compliance" value={health.breakdown.complianceScore} icon={Shield} />
        <BreakdownBar label="Tasks" value={health.breakdown.taskHealth} icon={CheckCircle2} />
        <BreakdownBar label="Links" value={health.breakdown.linkFreshness} icon={TrendingUp} />
        <BreakdownBar label="Activity" value={health.breakdown.activityRecency} icon={Activity} />
      </div>

      {/* Key details */}
      {health.details && (
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#F5F3F0] text-[10px] text-[#8C8078]">
          {health.details.overdueTasks > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <AlertTriangle className="w-3 h-3" />
              {health.details.overdueTasks} overdue
            </span>
          )}
          <span>{health.details.individuals} individuals</span>
          <span>{health.details.entities} entities</span>
        </div>
      )}
    </div>
  );
}

export default function ClientHealthScores({ onClientClick }) {
  const { token } = useAuth();
  const [data, setData] = useState({ scores: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!token) return;
    const fetchScores = async () => {
      try {
        const res = await api.getHealthScores(token);
        setData(res.data);
      } catch (e) {
        console.error('Failed to fetch health scores:', e);
      }
      setLoading(false);
    };
    fetchScores();
  }, [token]);

  const { summary, scores } = data;
  const filtered = filterStatus === 'all'
    ? scores
    : scores.filter(s => s.status === filterStatus);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="client-health-scores">
      {/* Section header with explainer */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-heading text-xl text-[#2C2420]">Client health</h2>
          <p className="text-xs text-[#8C8078] mt-0.5">
            Composite 0&ndash;100 signal of how well each relationship is being managed.
          </p>
        </div>
        <HealthScoreExplainer />
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="health-summary">
        <div className="bg-white rounded-lg border border-[#E6E0D9] p-3">
          <p className="text-xs text-[#8C8078] mb-1">Average Score</p>
          <p className="text-2xl font-heading text-[#2C2420]">{summary.averageScore || 0}</p>
        </div>
        <button
          onClick={() => setFilterStatus(filterStatus === 'critical' ? 'all' : 'critical')}
          className={cn(
            "rounded-lg border p-3 text-left transition-all",
            filterStatus === 'critical' ? "bg-red-50 border-red-300" : "bg-white border-[#E6E0D9] hover:border-red-300"
          )}
        >
          <p className="text-xs text-red-600 mb-1">Critical</p>
          <p className="text-2xl font-heading text-red-600">{summary.critical || 0}</p>
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'attention_needed' ? 'all' : 'attention_needed')}
          className={cn(
            "rounded-lg border p-3 text-left transition-all",
            filterStatus === 'attention_needed' ? "bg-orange-50 border-orange-300" : "bg-white border-[#E6E0D9] hover:border-orange-300"
          )}
        >
          <p className="text-xs text-orange-600 mb-1">Needs Attention</p>
          <p className="text-2xl font-heading text-orange-600">{summary.attentionNeeded || 0}</p>
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'good' ? 'all' : 'good')}
          className={cn(
            "rounded-lg border p-3 text-left transition-all",
            filterStatus === 'good' ? "bg-amber-50 border-amber-300" : "bg-white border-[#E6E0D9] hover:border-amber-300"
          )}
        >
          <p className="text-xs text-amber-600 mb-1">Good</p>
          <p className="text-2xl font-heading text-amber-600">{summary.good || 0}</p>
        </button>
        <button
          onClick={() => setFilterStatus(filterStatus === 'excellent' ? 'all' : 'excellent')}
          className={cn(
            "rounded-lg border p-3 text-left transition-all",
            filterStatus === 'excellent' ? "bg-green-50 border-green-300" : "bg-white border-[#E6E0D9] hover:border-green-300"
          )}
        >
          <p className="text-xs text-green-600 mb-1">Excellent</p>
          <p className="text-2xl font-heading text-green-600">{summary.excellent || 0}</p>
        </button>
      </div>

      {/* Client Health Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="health-cards-grid">
        {filtered.map(health => (
          <ClientHealthCard
            key={health.clientId}
            health={health}
            onClick={() => onClientClick?.(health.clientId)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Users className="w-10 h-10 text-[#D4CCC6] mx-auto mb-2" />
            <p className="text-sm text-[#8C8078]">No clients match the selected filter</p>
          </div>
        )}
      </div>
    </div>
  );
}
